/* eslint-disable max-lines-per-function */
import { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Discounts } from './Discounts'
import { InclusionInfoControl } from './InclusionInfoControl'
import { MedicalControl } from './MedicalControl'
import { NamesEmergencyControl } from './NamesEmergencyControl'
import { useDiscounts } from './useDiscounts'

import {
    FormNavigation,
    PhoneNumberControl,
    SelectControl
} from '@/components/form-controls'
import { useToast } from '@/context/ToastContext'
import { useAuthHook } from '@/hooks'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useInscriptionsApi } from '@/services/api/inscriptionsApi'
import { useResourcesApi } from '@/services/api/resourcesApi'
import { useAuthStore } from '@/store/authStore'
import { Discount } from '@/types'
import { MessageLevel, ErrorHandlingStrategy } from '@/types/api'
import { AboutYouFormData, FormsGroupsProps } from '@/types/forms/'
import { useFormProgress } from '@/utils'
import { isColCellphone } from '@/utils/validators'

// Helper functions to reduce complexity
const buildInclusionPayload = (data: AboutYouFormData) => ({
    ethnic_group: data.recognizesEthnicGroup === 'true' ? 1 : 0,
    ethnic_group_description:
        data.recognizesEthnicGroup === 'true' ? data.ethnicGroup : null,
    disability: data.hasDisability === 'true' ? 1 : 0,
    disability_description:
        data.hasDisability === 'true' ? data.disabilityType : null,
    group_special: 'N/A',
    disorder: 0,
    disorder_description: null
})

const handleApiResponse = (
    responses: any[],
    t: any,
    showToast: any,
    onNext: () => void,
    updateForms?: () => void
) => {
    const allSuccessful = responses.every((r) => r.status === 200)

    showToast({
        title: t('basicDataInformation.title'),
        message: allSuccessful ? t('forms.ui.save') : t('forms.ui.error'),
        type: allSuccessful ? 'success' : 'error'
    })
    if (allSuccessful) {
        // Actualizar el estado de los formularios antes de continuar
        if (updateForms) {
            updateForms()
        }
        onNext()
    }
}

const handleApiError = (error: any, t: any, showToast: any) => {
    console.error('Error al guardar los datos:', error)
    showToast({
        title: t('basicDataInformation.title'),
        message: t('forms.ui.error'),
        type: 'error'
    })
}

export const AboutYouForm = ({
    onNext,
    onPrevious,
    isReadOnly = false,
    onBlockedFieldClick
}: FormsGroupsProps): JSX.Element => {
    const { t } = useTranslation()
    const {
        control,
        handleSubmit,
        watch,
        reset,
        setValue,
        clearErrors,
        trigger
    } = useForm<AboutYouFormData>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            firstName: '',
            firstLastName: '',
            kinship: '',
            cellphone: '',
            eps: '',
            bloodType: '',
            discountType: '',
            recognizesEthnicGroup: '',
            hasDisability: '',
            ethnicGroup: '',
            disabilityType: ''
        }
    })
    const { idAspirant, idInscription } = useAuthHook()
    const { idAsesor } = useAuthStore()
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const resourcesApi = useResourcesApi({
        messageLevel: MessageLevel.ERRORS,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const inscriptionsApi = useInscriptionsApi({
        messageLevel: MessageLevel.ERRORS,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const [hasFetched, setHasFetched] = useState(false)
    const [kinship, setKinship] = useState<any[]>([])
    const [discounts, setDiscounts] = useState<Discount[]>([])
    const [uniqueDiscountType, setUniqueDiscountType] = useState(false)
    const [savedDiscountType, setSavedDiscountType] = useState<string | null>(
        null
    )
    const { showToast } = useToast()
    const {
        discountSelected,
        noAplicaDiscount,
        percentageDiscount,
        setDiscountSelected
    } = useDiscounts()
    const progress = useFormProgress<AboutYouFormData>({
        watch,
        excludedFields: [
            'idAspirant',
            'middleName',
            'secondLastName',
            'createdAt',
            'updatedAt'
        ],
        conditionalFields: [
            {
                field: 'ethnicGroup',
                dependsOn: 'recognizesEthnicGroup',
                condition: 'true'
            },
            {
                field: 'disabilityType',
                dependsOn: 'hasDisability',
                condition: 'true'
            }
        ]
    })

    useEffect(() => {
        if (hasFetched) {
            return
        }

        const fetchAllOptions = async () => {
            try {
                const [kinship] = await Promise.all([
                    resourcesApi.getRelationshipList()
                ])

                setKinship(
                    Array.isArray(kinship) && kinship.length > 0
                        ? kinship
                              .filter((item) => item?.value && item?.label)
                              .map((item, index) => ({
                                  key: `type-${item.value}-${index}`,
                                  value: item.value,
                                  label: item.label
                              }))
                        : []
                )

                setHasFetched(true)
            } catch (error) {
                console.error('Failed to fetch resources:', error)
            }
        }

        fetchAllOptions()
    }, [resourcesApi, reset])

    // Fetch discounts when recognizesEthnicGroup changes
    const recognizesEthnicGroupValue = useWatch({
        control,
        name: 'recognizesEthnicGroup'
    })
    const previousEthnicGroupValue = useRef<string>('')

    useEffect(() => {
        // Skip if value hasn't changed or is empty
        if (
            !recognizesEthnicGroupValue ||
            !idInscription ||
            recognizesEthnicGroupValue === previousEthnicGroupValue.current
        ) {
            previousEthnicGroupValue.current = recognizesEthnicGroupValue || ''

            return
        }

        previousEthnicGroupValue.current = recognizesEthnicGroupValue

        // Save current discountType before clearing (in case we need to restore it)
        const currentDiscountType = watch('discountType')
        const discountToRestore = savedDiscountType || currentDiscountType

        // Clear previous discounts data when value changes
        setDiscounts([])
        setValue('discountType', '', { shouldValidate: false })
        clearErrors('discountType')
        setUniqueDiscountType(false)
        setDiscountSelected(null)

        // Store discount to restore after fetching
        const discountToRestoreRef = discountToRestore

        const fetchDiscounts = async () => {
            try {
                // Convert 'true'/'false' string to boolean
                const recognizesEthnicGroupBoolean =
                    recognizesEthnicGroupValue === 'true'

                const { data } = await resourcesApi.getDiscounts(
                    idInscription || '',
                    recognizesEthnicGroupBoolean
                )

                if (data && data.length > 0) {
                    if (data.length === 1) {
                        setDiscounts(data)
                        setValue('discountType', data[0].categoria)
                        setUniqueDiscountType(true)
                        setSavedDiscountType(null)
                    } else {
                        data.push(noAplicaDiscount())
                        setDiscounts(data)
                        setUniqueDiscountType(false)

                        // Restore saved discountType if it exists in the new options
                        // eslint-disable-next-line max-depth
                        if (discountToRestoreRef) {
                            const existsInOptions = data.some(
                                (discount: Discount) =>
                                    discount.categoria === discountToRestoreRef
                            )

                            // eslint-disable-next-line max-depth
                            if (existsInOptions) {
                                // Use setTimeout to ensure discounts state is updated first
                                setTimeout(() => {
                                    setValue(
                                        'discountType',
                                        discountToRestoreRef,
                                        { shouldValidate: false }
                                    )
                                    setSavedDiscountType(null)
                                }, 100)
                            } else {
                                setSavedDiscountType(null)
                            }
                        }
                    }
                } else {
                    setDiscounts([noAplicaDiscount()])
                    setUniqueDiscountType(false)
                }
            } catch (error) {
                console.error('Failed to fetch discounts:', error)
                setDiscounts([noAplicaDiscount()])
                setUniqueDiscountType(false)
            }
        }

        fetchDiscounts()
    }, [
        recognizesEthnicGroupValue,
        idInscription,
        resourcesApi,
        inscriptionsApi,
        setValue,
        savedDiscountType,
        watch
    ])

    useEffect(() => {
        const loadAboutYouData = async (): Promise<void> => {
            if (!idAspirant) {
                return
            }
            const [
                responseEmergencyContactData,
                responseDiscountsData,
                responseMedicalData,
                responseInclusionData
            ] = await Promise.all([
                aspirantsApi.getEmergencyContactData(idAspirant),
                aspirantsApi.getDiscountsData(idInscription || 0),
                aspirantsApi.getMedicalData(idAspirant),
                aspirantsApi.getInclusionData(idAspirant)
            ])

            if (
                responseEmergencyContactData.status === 200 &&
                responseDiscountsData.status === 200 &&
                responseMedicalData.status === 200 &&
                responseInclusionData.status === 200
            ) {
                // Save discountType to restore it after discounts are loaded
                const savedDiscount = responseDiscountsData.data.categoria

                if (savedDiscount) {
                    setSavedDiscountType(savedDiscount)
                }

                reset({
                    ...responseEmergencyContactData.data,
                    discountType: '', // Don't set discountType yet, will be set after discounts load
                    eps: responseMedicalData.data.eps,
                    bloodType: responseMedicalData.data.bloodType,
                    recognizesEthnicGroup: responseInclusionData.data
                        .ethnic_group
                        ? 'true'
                        : 'false',
                    hasDisability: responseInclusionData.data.disability
                        ? 'true'
                        : 'false',
                    ethnicGroup:
                        responseInclusionData.data.ethnic_group_description ||
                        '',
                    disabilityType:
                        responseInclusionData.data.disability_description || ''
                })
            }
        }

        loadAboutYouData()
    }, [idAspirant, idInscription, reset])

    // Sync discountType when discounts are loaded and we have a saved value
    useEffect(() => {
        if (
            savedDiscountType &&
            discounts.length > 0 &&
            watch('discountType') !== savedDiscountType
        ) {
            const existsInOptions = discounts.some(
                (discount: Discount) => discount.categoria === savedDiscountType
            )

            if (existsInOptions) {
                // Small delay to ensure discounts are fully loaded in the component
                setTimeout(() => {
                    setValue('discountType', savedDiscountType, {
                        shouldValidate: false
                    })
                    setSavedDiscountType(null)
                }, 100)
            } else {
                setSavedDiscountType(null)
            }
        }
    }, [discounts, savedDiscountType, setValue, watch])

    const onSubmit: SubmitHandler<AboutYouFormData> = async (data) => {
        if (!idAspirant) {
            return
        }

        try {
            // Ejecutar todas las peticiones en paralelo con Promise.all
            const [
                responseEmergencyContact,
                responseDiscounts,
                responseMedical,
                responseInclusion
            ] = await Promise.all([
                // Primera petición: datos de contacto de emergencia
                aspirantsApi.postEmergencyContactData(
                    idAspirant,
                    {
                        firstName: data.firstName,
                        middleName: data.middleName,
                        firstLastName: data.firstLastName,
                        secondLastName: data.secondLastName,
                        kinship: data.kinship,
                        cellphone: data.cellphone
                    },
                    idAsesor || 0
                ),

                // Segunda petición: datos de descuentos
                aspirantsApi.postDiscountsData(
                    idInscription || 0,
                    idAspirant,
                    discountSelected || noAplicaDiscount(),
                    idAsesor || 0
                ),
                // Tercera petición: datos médicos
                aspirantsApi.postMedicalData(
                    idAspirant,
                    {
                        eps: data.eps,
                        bloodType: data.bloodType
                    },
                    idAsesor || 0
                ),
                aspirantsApi.postInclusionData(
                    idAspirant,
                    buildInclusionPayload(data),
                    idAsesor || 0
                )
            ])

            // Verificar el éxito de todas las peticiones
            handleApiResponse(
                [
                    responseEmergencyContact,
                    responseDiscounts,
                    responseMedical,
                    responseInclusion
                ],
                t,
                showToast,
                onNext,
                () => {
                    // Función para actualizar el estado de formularios
                    if (idAspirant) {
                        aspirantsApi
                            .getStatusForms(idAspirant, idInscription || 0)
                            .then(() =>
                                console.log(
                                    'Form status updated after AboutYou completion'
                                )
                            )
                            .catch((error) =>
                                console.error(
                                    'Error updating form status:',
                                    error
                                )
                            )
                    }
                }
            )
        } catch (error) {
            handleApiError(error, t, showToast)
        }
    }

    return (
        <div className={`relative ${isReadOnly ? 'opacity-60' : ''}`}>
            {isReadOnly && (
                <>
                    <div className="absolute inset-0 z-10 bg-gray-200 bg-opacity-50 cursor-not-allowed" />
                    <div
                        className="absolute inset-0 z-20 bg-transparent cursor-pointer"
                        onClick={onBlockedFieldClick}
                    />
                    <div className="absolute top-2 right-2 z-30 bg-yellow-100 border border-yellow-400 text-yellow-800 px-2 py-1 rounded text-xs">
                        🔒 Campos bloqueados - Click para más información
                    </div>
                </>
            )}
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="w-full space-y-4"
            >
                <InclusionInfoControl
                    control={control}
                    setValue={setValue as { (name: string, value: any): void }}
                    clearErrors={clearErrors}
                    idAspirant={idAspirant || undefined}
                />
                <Discounts
                    control={control}
                    optionsDiscounts={discounts}
                    watchDiscountType={watch('discountType')}
                    uniqueDiscountType={uniqueDiscountType}
                    percentageDiscount={percentageDiscount}
                    watchRecognizesEthnicGroup={recognizesEthnicGroupValue}
                    setValue={setValue}
                    clearErrors={clearErrors}
                />
                <MedicalControl
                    control={control}
                    trigger={trigger}
                />
                <NamesEmergencyControl control={control} />
                <SelectControl
                    name="kinship"
                    control={control}
                    options={kinship}
                    label={t('contactEmergency.kinship')}
                    rules={{
                        required: t('contactEmergency.required.kinshipRequired')
                    }}
                />
                <PhoneNumberControl
                    name="cellphone"
                    control={control}
                    label={t('forms.common.cellphoneNumber')}
                    rules={{
                        validate: (value: string) =>
                            isColCellphone(value) ||
                            t('forms.common.invalidPhoneNumber'),
                        required: t(
                            'forms.common.required.cellphoneNumberRequired'
                        )
                    }}
                />

                <FormNavigation
                    onSubmit={handleSubmit(onSubmit)}
                    onPrevious={onPrevious}
                    progress={progress}
                    submitLabel={t('forms.ui.nextPage')}
                    triggerValidation={trigger}
                />
            </form>
        </div>
    )
}
